// Canvas API explorer: lets admins read Canvas data for a context without a production shell.
// The Explore tab's buttons run the read-only CanvasConnectClient methods registered in
// links/lib/lms_client/canvas_api_explorer.py; results drill down into related calls (modules ->
// items -> page HTML), and a custom request GETs any /api/ path on the context's Canvas instance.
// The Post a grade tab (PostGrade.js) is the explorer's one write: it sends a student's grade.

import React, { Fragment, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Button } from '@CommonComponents/buttons'
import { client } from '@Utils'
import { canvasOrgKeyManagement as endpoints } from '@URLS'
import PostGrade from './PostGrade'
import ResultView from './ResultView'
import {
  drilldownParams,
  initialParams,
  resolvedPath,
  rowLabel,
} from './explorer'

const errorFrom = err => {
  const data = (err.response && err.response.data) || {}
  return {
    message: data.error || err.message || 'The request failed.',
    canvasStatus: data.canvasStatus,
    canvasBody: data.canvasBody,
  }
}

const groupActions = actions =>
  actions.reduce((groups, action) => {
    const group = groups.find(g => g.name === action.group)
    if (group) group.actions.push(action)
    else groups.push({ name: action.group, actions: [action] })
    return groups
  }, [])

const TABS = [
  { key: 'explore', label: 'Explore' },
  { key: 'post-grade', label: 'Post a grade' },
]

// Bootstrap nav-tabs with the tab pattern's roles and arrow-key movement
const Tabs = ({ selected, onSelect }) => {
  const refs = useRef({})
  const onKeyDown = e => {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key]
    if (!step) return
    e.preventDefault()
    const index = TABS.findIndex(tab => tab.key === selected)
    const next = TABS[(index + step + TABS.length) % TABS.length]
    onSelect(next.key)
    refs.current[next.key].focus()
  }
  return (
    <ul
      className="nav nav-tabs mb-3"
      role="tablist"
      aria-label="Explorer views"
    >
      {TABS.map(tab => (
        <li
          key={tab.key}
          role="presentation"
          className={selected === tab.key ? 'active' : ''}
        >
          <a
            href={`#explorer-panel-${tab.key}`}
            id={`explorer-tab-${tab.key}`}
            ref={el => (refs.current[tab.key] = el)}
            role="tab"
            aria-selected={selected === tab.key}
            aria-controls={`explorer-panel-${tab.key}`}
            tabIndex={selected === tab.key ? 0 : -1}
            onClick={e => {
              e.preventDefault()
              onSelect(tab.key)
            }}
            onKeyDown={onKeyDown}
          >
            {tab.label}
          </a>
        </li>
      ))}
    </ul>
  )
}

Tabs.propTypes = {
  selected: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
}

const ActionForm = ({ action, onRun, onCancel }) => {
  const [params, setParams] = useState(initialParams(action))
  const setParam = (name, value) => setParams({ ...params, [name]: value })
  const submit = e => {
    e.preventDefault()
    onRun(action, params)
  }
  return (
    <form onSubmit={submit}>
      {action.params.map(param =>
        param.type === 'boolean' ? (
          <div className="form-check mb-3" key={param.name}>
            <input
              id={`explorer-param-${param.name}`}
              className="form-check-input"
              type="checkbox"
              checked={params[param.name]}
              onChange={e => setParam(param.name, e.target.checked)}
            />
            <label
              className="form-check-label"
              htmlFor={`explorer-param-${param.name}`}
            >
              {param.label}
            </label>
          </div>
        ) : (
          <div className="form-group" key={param.name}>
            <label htmlFor={`explorer-param-${param.name}`}>
              {param.label}
            </label>
            <input
              id={`explorer-param-${param.name}`}
              className="form-control"
              type="text"
              required
              value={params[param.name]}
              onChange={e => setParam(param.name, e.target.value)}
            />
          </div>
        )
      )}
      <div className="d-flex" style={{ gap: '8px' }}>
        <Button type="submit" className="me-btn me-btn-primary me-btn-sm">
          Run {action.label}
        </Button>
        <Button
          className="me-btn me-btn-secondary me-btn-sm"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}

ActionForm.propTypes = {
  action: PropTypes.object.isRequired,
  onRun: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

const ApiExplorer = ({ match }) => {
  const { orgId, contextId } = match.params
  const [actions, setActions] = useState([])
  const [context, setContext] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [tab, setTab] = useState('explore')
  const [selectedAction, setSelectedAction] = useState(null)
  const [customPath, setCustomPath] = useState('/api/v1/courses')
  const [allPages, setAllPages] = useState(true)
  // the drill-down trail: each step is one call and its result; the last one is shown
  const [steps, setSteps] = useState([])
  const nextStepId = useRef(1)
  const pathInput = useRef(null)

  useEffect(() => {
    client
      .get(endpoints.getApiExplorerActions({ contextId }))
      .then(resp => {
        setActions(resp.data.actions)
        setContext(resp.data.context)
        if (resp.data.context.canvasCourseId) {
          setCustomPath(`/api/v1/courses/${resp.data.context.canvasCourseId}`)
        }
      })
      .catch(err => setLoadError(errorFrom(err).message))
  }, [contextId])

  const actionsByKey = Object.fromEntries(actions.map(a => [a.key, a]))

  // run a call; append it to the trail when drilling down, otherwise start a new trail
  const runStep = (step, request, append) => {
    const id = nextStepId.current++
    const newStep = { ...step, id, status: 'loading' }
    setSteps(prev => (append ? [...prev, newStep] : [newStep]))
    request
      .then(resp =>
        setSteps(prev =>
          prev.map(s =>
            s.id === id ? { ...s, status: 'done', response: resp.data } : s
          )
        )
      )
      .catch(err =>
        setSteps(prev =>
          prev.map(s =>
            s.id === id ? { ...s, status: 'error', error: errorFrom(err) } : s
          )
        )
      )
  }

  const runAction = (action, params, label, append = false) => {
    setSelectedAction(null)
    runStep(
      {
        action,
        params,
        label: label || action.label,
        path: resolvedPath(action, context && context.canvasCourseId, params),
      },
      client.post(endpoints.runApiExplorerAction({ contextId }), {
        action: action.key,
        params,
      }),
      append
    )
  }

  const runRaw = (path, label, append = false) => {
    runStep(
      { action: null, path, label: label || `GET ${path}` },
      client.post(endpoints.runApiExplorerRequest({ contextId }), {
        path,
        allPages,
      }),
      append
    )
  }

  const chooseAction = action => {
    if (action.params.some(param => param.type !== 'boolean')) {
      setSelectedAction(action)
    } else {
      runAction(action, initialParams(action))
    }
  }

  const drill = (drilldown, row) => {
    const action = actionsByKey[drilldown.action]
    const params = {
      ...initialParams(action),
      ...drilldownParams(drilldown, row),
    }
    runAction(action, params, `${drilldown.label}: ${rowLabel(row)}`, true)
  }

  const editAsCustom = path => {
    setCustomPath(path)
    if (pathInput.current) pathInput.current.focus()
  }

  const current = steps[steps.length - 1]
  const backLink = `/account/admin_org_key_management/-/organizations/${orgId}/contexts/${contextId}`

  if (loadError) {
    return (
      <section className="me-card" aria-labelledby="explorer-title">
        <header className="me-card-header">
          <div className="me-card-heading">
            <h2 id="explorer-title" className="me-card-title">
              Canvas API explorer
            </h2>
            <div className="me-card-meta">
              <Link className="me-btn-link-inline" to={backLink}>
                Back to context
              </Link>
            </div>
          </div>
        </header>
        <div className="me-card-alert me-card-alert-danger" role="alert">
          <i
            className="fa-regular fa-triangle-exclamation"
            aria-hidden="true"
          />
          <span>{loadError}</span>
        </div>
      </section>
    )
  }
  if (!context) return <p>Loading ...</p>

  return (
    <Fragment>
      <section className="me-card" aria-labelledby="explorer-title">
        <header className="me-card-header">
          <div className="me-card-heading">
            <span className="me-card-eyebrow">Canvas API explorer</span>
            <h2 id="explorer-title" className="me-card-title">
              {context.contextTitle || 'Untitled context'}
              {context.canvasCourseId && (
                <span className="me-card-title-note">
                  Canvas course {context.canvasCourseId}
                </span>
              )}
            </h2>
            <div className="me-card-meta">
              <span>{context.domain}</span>
              {context.instructor && <span>Token: {context.instructor}</span>}
              <Link className="me-btn-link-inline" to={backLink}>
                Back to context
              </Link>
            </div>
          </div>
        </header>
        {!context.canvasCourseId && (
          <div className="me-card-alert me-card-alert-warning" role="note">
            <i
              className="fa-regular fa-triangle-exclamation"
              aria-hidden="true"
            />
            <span>
              This context has no Canvas course ID, so only custom requests will
              work, and grades can&apos;t be posted.
            </span>
          </div>
        )}
      </section>

      <Tabs selected={tab} onSelect={setTab} />

      {/* both panels stay mounted so switching tabs keeps each one's state */}
      <div
        id="explorer-panel-explore"
        role="tabpanel"
        aria-labelledby="explorer-tab-explore"
        hidden={tab !== 'explore'}
      >
        <section className="me-card" aria-labelledby="explorer-methods-title">
          <header className="me-card-header">
            <div className="me-card-heading">
              <h2 id="explorer-methods-title" className="me-card-title">
                Client methods
              </h2>
              <div className="me-card-meta">
                <span>Read-only CanvasConnectClient calls for this course</span>
              </div>
            </div>
          </header>
          {groupActions(actions).map(group => (
            <div className="me-card-section" key={group.name}>
              <div className="me-card-section-header">
                <h3 className="me-card-section-title">{group.name}</h3>
              </div>
              <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                {group.actions.map(action => (
                  <Button
                    key={action.key}
                    className="me-btn me-btn-secondary me-btn-sm"
                    title={action.path}
                    disabled={!context.canvasCourseId}
                    onClick={() => chooseAction(action)}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            </div>
          ))}
          {selectedAction && (
            <div className="me-card-section">
              <div className="me-card-section-header">
                <h3 className="me-card-section-title">
                  {selectedAction.label}
                </h3>
              </div>
              <p className="text-muted">
                <code>{selectedAction.path}</code>
              </p>
              <ActionForm
                key={selectedAction.key}
                action={selectedAction}
                onRun={(action, params) => runAction(action, params)}
                onCancel={() => setSelectedAction(null)}
              />
            </div>
          )}
        </section>

        <section className="me-card" aria-labelledby="explorer-custom-title">
          <header className="me-card-header">
            <div className="me-card-heading">
              <h2 id="explorer-custom-title" className="me-card-title">
                Custom GET request
              </h2>
              <div className="me-card-meta">
                <span>
                  Any Canvas REST path under /api/, sent with the context&apos;s
                  token
                </span>
                <a
                  className="me-btn-link-inline"
                  href="https://canvas.instructure.com/doc/api/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Canvas API docs
                </a>
              </div>
            </div>
          </header>
          <div className="me-card-body">
            <form
              onSubmit={e => {
                e.preventDefault()
                runRaw(customPath.trim())
              }}
            >
              <div className="form-group">
                <label htmlFor="explorer-custom-path">Path</label>
                <input
                  id="explorer-custom-path"
                  ref={pathInput}
                  className="form-control"
                  type="text"
                  required
                  placeholder="/api/v1/courses/123/pages?sort=title"
                  value={customPath}
                  onChange={e => setCustomPath(e.target.value)}
                />
              </div>
              <div className="form-check mb-3">
                <input
                  id="explorer-all-pages"
                  className="form-check-input"
                  type="checkbox"
                  checked={allPages}
                  onChange={e => setAllPages(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="explorer-all-pages"
                >
                  Follow pagination and return every page of a list
                </label>
              </div>
              <Button type="submit" className="me-btn me-btn-primary me-btn-sm">
                Send GET
              </Button>
            </form>
          </div>
        </section>

        {current && (
          <section
            className="me-card"
            aria-labelledby="explorer-result-title"
            aria-live="polite"
          >
            <header className="me-card-header">
              <div className="me-card-heading">
                <span className="me-card-eyebrow">Result</span>
                <h2 id="explorer-result-title" className="me-card-title">
                  {current.label}
                </h2>
                <div className="me-card-meta">
                  <code>GET {current.path}</code>
                </div>
              </div>
              <div className="me-card-header-end">
                <Button
                  className="me-btn me-btn-ghost me-btn-sm"
                  icon="fa-regular fa-pen-to-square"
                  onClick={() => editAsCustom(current.path)}
                >
                  Edit as custom request
                </Button>
              </div>
            </header>
            {current.status === 'error' && (
              <div className="me-card-alert me-card-alert-danger" role="alert">
                <i
                  className="fa-regular fa-triangle-exclamation"
                  aria-hidden="true"
                />
                <span>{current.error.message}</span>
              </div>
            )}
            <div className="me-card-body">
              {steps.length > 1 && (
                <nav aria-label="Drill-down trail" className="mb-3">
                  {steps.map((step, i) => (
                    <Fragment key={step.id}>
                      {i > 0 && (
                        <i
                          className="fa-regular fa-angle-right mx-2 text-muted"
                          aria-hidden="true"
                        />
                      )}
                      {i < steps.length - 1 ? (
                        <button
                          type="button"
                          className="me-btn-link-inline"
                          onClick={() => setSteps(steps.slice(0, i + 1))}
                        >
                          {step.label}
                        </button>
                      ) : (
                        <span aria-current="step">{step.label}</span>
                      )}
                    </Fragment>
                  ))}
                </nav>
              )}
              <ResultView
                step={current}
                onDrill={drill}
                onRawPath={path => runRaw(path, null, true)}
              />
            </div>
          </section>
        )}
      </div>

      <div
        id="explorer-panel-post-grade"
        role="tabpanel"
        aria-labelledby="explorer-tab-post-grade"
        hidden={tab !== 'post-grade'}
      >
        <PostGrade
          contextId={contextId}
          canPost={Boolean(context.canvasCourseId)}
        />
      </div>
    </Fragment>
  )
}

ApiExplorer.propTypes = {
  match: PropTypes.object.isRequired,
}

export default ApiExplorer
